import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { SEO } from "../components/SEO";

export function NotFoundPage() {
  return (
    <>
      <SEO
        title="Page Not Found"
        description="The page you're looking for doesn't exist or has been moved. Return to the Valkyrie Robotics homepage to explore our VEX Robotics programs."
        noindex={true}
      />
      <div className="bg-neutral-900 min-h-screen flex items-center justify-center px-4">
        <div className="text-center max-w-2xl mx-auto">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
          >
            {/* 404 Number */}
            <motion.h1
              className="text-8xl md:text-9xl font-bold text-brand-400 mb-4"
              initial={{ scale: 0.8 }}
              animate={{ scale: 1 }}
              transition={{ duration: 0.8, delay: 0.2 }}
            >
              404
            </motion.h1>

            {/* Main Message */}
            <motion.h2
              className="text-2xl md:text-3xl font-semibold text-white mb-4"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ duration: 0.6, delay: 0.4 }}
            >
              Page Not Found
            </motion.h2>

            <motion.p
              className="text-gray-400 text-lg mb-8 leading-relaxed"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ duration: 0.6, delay: 0.6 }}
            >
              The page you're looking for doesn't exist or has been moved. Don't
              worry though - our VEX Robotics programs are still here!
            </motion.p>

            {/* Action Buttons */}
            <motion.div
              className="flex flex-col sm:flex-row gap-4 justify-center items-center"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.8 }}
            >
              <Link
                to="/"
                className="inline-flex items-center px-6 py-3 bg-brand-600 text-neutral-900 font-medium rounded-lg hover:bg-brand-500 transition-all duration-200 hover:scale-105 active:scale-95"
              >
                <svg
                  className="w-5 h-5 mr-2"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M3 12l2-2m0 0l7-7 7 7M5 10v10a1 1 0 001 1h3m10-11l2 2m-2-2v10a1 1 0 01-1 1h-3m-6 0a1 1 0 001-1v-4a1 1 0 011-1h2a1 1 0 011 1v4a1 1 0 001 1m-6 0h6"
                  />
                </svg>
                Go Home
              </Link>

              <Link
                to="/blog"
                className="inline-flex items-center px-6 py-3 border border-neutral-700 text-white font-medium rounded-lg hover:border-neutral-600 hover:bg-neutral-800 transition-all duration-200"
              >
                <svg
                  className="w-5 h-5 mr-2"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M19 20H5a2 2 0 01-2-2V6a2 2 0 012-2h10a2 2 0 012 2v1m2 13a2 2 0 01-2-2V7m2 13a2 2 0 002-2V9a2 2 0 00-2-2h-2m-4-3H9M7 16h6M7 8h6v4H7V8z"
                  />
                </svg>
                Read Our Blog
              </Link>
            </motion.div>

            {/* Quick Links */}
            <motion.div
              className="mt-12 pt-8 border-t border-neutral-800"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ duration: 0.6, delay: 1.0 }}
            >
              <p className="text-gray-500 text-sm mb-4">Popular Pages:</p>
              <div className="flex flex-wrap justify-center gap-3 text-sm">
                <Link
                  to="/iq"
                  className="text-brand-400 hover:text-brand-300 transition-colors duration-200"
                >
                  VEX IQ Program
                </Link>
                <span className="text-gray-600">•</span>
                <Link
                  to="/v5"
                  className="text-brand-400 hover:text-brand-300 transition-colors duration-200"
                >
                  VEX V5 Program
                </Link>
                <span className="text-gray-600">•</span>
                <Link
                  to="/summer-camp"
                  className="text-brand-400 hover:text-brand-300 transition-colors duration-200"
                >
                  Summer Camp
                </Link>
                <span className="text-gray-600">•</span>
                <Link
                  to="/contact"
                  className="text-brand-400 hover:text-brand-300 transition-colors duration-200"
                >
                  Contact Us
                </Link>
              </div>
            </motion.div>
          </motion.div>
        </div>
      </div>
    </>
  );
}
